// Social counsel: who should self pursue? Picks the max urge-to-chat free
// NPC in range (not just the nearest body). Pure given relationship +
// recency lookups, so tests drive it with fakes and the game wires refs.
import { urgeToChat } from '../sim/relationships.js';
import type { RuntimeNpc } from '../types.js';

// Unordered pair key for the last-chat map (direction doesn't matter here).
export function pairKey(a: string, b: string): string {
  return a < b ? `${a}|${b}` : `${b}|${a}`;
}

export interface CounselDeps {
  relOf: (selfId: string, otherId: string) => { affinity: number; attraction: number } | null;
  msSinceChat: (selfId: string, otherId: string) => number | null;
  maxDist?: number; // social horizon (default 300, matches urge distance falloff)
  minUrge?: number; // 0-100; below this, drift instead of pursue (default 5)
}

export function buildCounsel(deps: CounselDeps): (selfId: string, others: RuntimeNpc[]) => RuntimeNpc | null {
  const maxDist = deps.maxDist ?? 300;
  const minUrge = deps.minUrge ?? 5;
  return (selfId: string, others: RuntimeNpc[]) => {
    const self = others.find((o) => o.npcId === selfId);
    if (!self) return null;
    let best: RuntimeNpc | null = null;
    let bestU = minUrge;
    for (const o of others) {
      if (o.npcId === selfId || o.busyWith || o.restingBed) continue;
      const rel = deps.relOf(selfId, o.npcId);
      if (!rel) continue;
      const d = Math.hypot(o.x - self.x, o.y - self.y);
      if (d > maxDist) continue;
      const u = urgeToChat(rel.affinity, rel.attraction, deps.msSinceChat(selfId, o.npcId), d).value;
      if (u > bestU) {
        bestU = u;
        best = o;
      }
    }
    return best;
  };
}
